import { List, ListItem, ListItemIcon, Stack, Typography, Collapse, Box, Chip, ListItemSecondaryAction } from '@mui/material';
import { BarcodeOutlined, GlobalOutlined, UserOutlined  } from '@ant-design/icons';
import { useTheme } from '@mui/material/styles';
import CopyTextIconButton from 'components/CopyTextIconButton';
import { Company, Application, Owner } from 'graphql/types';
import AssetLogo from 'sections/AssetLogo';
import PhoneNoRenderer from 'utils/getFormattedNumbers';
import { MerchantContactInfoList } from './MerchantContactInfoList';
import CustomTooltip from 'components/CustomTooltip';
import { User, Rights, useUsersQuery, useCompanyOwnersQuery } from 'graphql/types';
import { useState } from 'react';
import { getUserProfileFullName } from 'utils/users';
import useAuth from 'hooks/useAuth';

interface MerchantProfileListProps {
  merchant?: Company;
  application?: Application;
  isRowExpanded?: boolean;
  isHovered?: boolean;
  isCollapsed: boolean;
  debouncedSetHoveredMerchant?: (value: boolean) => void;
  disableContactInfoClamp?: boolean;
}

export default function MerchantProfileList({
  merchant,
  isHovered,
  isRowExpanded,
  application,
  isCollapsed = false,
  debouncedSetHoveredMerchant,
  disableContactInfoClamp = false
}: MerchantProfileListProps) {
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const canListUsers = currentUser?.rights?.some((r) =>
    [Rights.ListUsers, Rights.ManageSyndications, Rights.ManageUsers].includes(r as Rights)
  );
  const { data: usersData } = useUsersQuery({
    skip: !merchant?.id || !canListUsers,
    variables: {
      companyId: merchant?.id,
      sortArgs: [
        {
          field: 'name',
          direction: 'asc'
        }
      ]
    },
    fetchPolicy: 'network-only'
  });
  const { data: companyOwnersData } = useCompanyOwnersQuery({
    skip: !merchant?.id,
    variables: { companyId: merchant?.id! },
    fetchPolicy: 'network-only'
  });

  const legalName = merchant?.legalName || application?.merchantCompanyLegalName;
  const dba = merchant?.dba || application?.merchantCompanyDba;
  const web = merchant?.www || application?.merchantBizWeb;
  const associatedUsers = (usersData?.users?.data as User[]) || [];
  const ownerUsers = associatedUsers.filter((user) => user.isOwner === true);
  const companyOwners = (companyOwnersData?.companyOwners as Owner[]) || [];
  const allOwners = [
    ...ownerUsers.map((u) => ({
      id: u.id,
      fullName: getUserProfileFullName(u),
      email: u.email,
      phone: u.data?.bizPhone
    })),
    ...companyOwners.map((o) => ({
      id: o.id,
      fullName: [o?.firstName, o?.lastName].filter(Boolean).join(' '),
      email: o.bizEmail,
      phone: o.cellPhone || o.bizPhone
    }))
  ];
  const taxId = merchant?.taxId || application?.merchantTaxId;
  const [legalNameHovered, setLegalNameHovered] = useState(false);
  const [dbaHovered, setDbaHovered] = useState(false);
  const [hoveredOwnerId, setHoveredOwnerId] = useState<string | null>(null);
  const [webHovered, setWebHovered] = useState(false);
  const [taxIdHovered, setTaxIdHovered] = useState(false);
  const merchantContactInfo = (
      <Box
          sx={{
            ...(disableContactInfoClamp || !isCollapsed
                ? {
                  height: 'auto',
                  maxHeight: 'none',
                  overflowY: 'visible',
                  overflowX: 'visible'
                }
                : {
                  height: 70,
                  overflowY: 'auto',
                  overflowX: 'hidden'
                })
          }}
      >
      <MerchantContactInfoList items={merchant?.email || application?.merchantBizEmails} label="Email" isCollapsed={isCollapsed} />
      <MerchantContactInfoList
        items={merchant?.phone || application?.merchantBizPhones}
        label="Phone"
        isCollapsed={isCollapsed}
        renderer={(val) => <PhoneNoRenderer value={val} />}
      />
      {(merchant?.www || application?.merchantBizWeb) && isCollapsed && (
        <ListItem
          disableGutters
          sx={{ m: 0 }}
          onMouseEnter={() => setWebHovered(true)}
          onMouseLeave={() => setWebHovered(false)}
        >
          <ListItemIcon sx={{ minWidth: 32, mr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <GlobalOutlined style={{ color: theme.palette?.slateblue?.lighter }} />
          </ListItemIcon>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5} flex={1} sx={{ minWidth: 0, height: 22 }}>
            <Typography
              align="left"
              style={{ color: theme.palette?.lightskyblue?.main || '#87CEFA' }}
              noWrap
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                '@media (min-width: 1280px) and (max-width: 1700px)': { maxWidth: '110px' },
                '@media (min-width: 1700px) and (max-width: 1950px)': { maxWidth: '150px' }
              }}
            >
              <a
                href={web ? (web.startsWith('http') ? web : `https://${web}`) : undefined}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                {web}
              </a>
            </Typography>
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                minWidth: 40,
                visibility: webHovered && web ? 'visible' : 'hidden'
              }}
            >
              {web && <CopyTextIconButton textToCopy={web} dispatchText="Web address" />}
            </Box>
          </Stack>
        </ListItem>
      )}
      {taxId && isCollapsed && (
        <ListItem
          disableGutters
          sx={{ m: 0 }}
          onMouseEnter={() => setTaxIdHovered(true)}
          onMouseLeave={() => setTaxIdHovered(false)}
        >
          <ListItemIcon sx={{ minWidth: 32, mr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BarcodeOutlined style={{ fontSize: 12, color: theme.palette?.slateblue?.lighter }} />
          </ListItemIcon>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={0.5}
            flex={1}
            sx={{ minWidth: 0, height: 22 }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 12, color: theme.palette?.slateblue?.lighter, flexShrink: 0 }}>TAX ID</Typography>
              <Typography sx={{ fontSize: 12 }} noWrap>
                {taxId}
              </Typography>
            </Stack>
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                minWidth: 40,
                visibility: taxIdHovered ? 'visible' : 'hidden'
              }}
            >
              <CopyTextIconButton textToCopy={taxId || ''} dispatchText="Tax Id" />
            </Box>
          </Stack>
        </ListItem>
      )}
    </Box>
  );
  return (
    <List component="nav" aria-label="Merchant profile" sx={{ py: 0.75, '& .MuiListItem-root': { p: 0 } }}>
      <Stack
        spacing={0.5}
        textAlign="left"
        onMouseEnter={() => debouncedSetHoveredMerchant?.(true)}
        onMouseLeave={() => debouncedSetHoveredMerchant?.(false)}
      >
        <ListItem>
          <Stack direction="row" spacing={1} alignItems="center" width="100%" my={legalName !== dba ? 0 : 1}>
            <AssetLogo logo={merchant?.logo} size="sm" name={legalName || ''} />
            <Stack justifyContent="flex-start" flex={1} sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                height={5}
                mt={legalName !== dba ? 1.5 : 0}
                onMouseEnter={() => setLegalNameHovered(true)}
                onMouseLeave={() => setLegalNameHovered(false)}
              >
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <CustomTooltip
                    title={legalName}
                    placement="top"
                  >
                    <Typography
                      noWrap
                      sx={{
                        '@media (min-width: 1280px) and (max-width: 1700px)': {
                          maxWidth: '150px'
                        }
                      }}
                    >
                      {legalName}
                    </Typography>
                  </CustomTooltip>
                </Box>
                {(!isCollapsed || legalNameHovered ) && <CopyTextIconButton textToCopy={legalName || ''} dispatchText="Legal Name" />}
              </Stack>
              {legalName !== dba && (
                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" mt={1} sx={{ height: 22 }} onMouseEnter={() => setDbaHovered(true)}
                  onMouseLeave={() => setDbaHovered(false)}>
                  <Stack direction="row" alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                    <Chip
                      label="DBA"
                      sx={{
                        fontSize: !isRowExpanded ? '11px !important' : undefined,
                        bgcolor: 'slateblue.dark',
                        '& .MuiChip-label': {
                          px: '0',
                          color: 'lightgray'
                        },
                        width: 30,
                        height: 15,
                        mr: 0.5,
                        flexShrink: 0
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      <CustomTooltip
                        title={dba}
                      >
                        <Typography
                          noWrap
                          sx={{
                            fontSize: !isRowExpanded ? '11px !important' : undefined,
                            color: 'slateblue.main',
                            '@media (min-width: 1280px) and (max-width: 1700px)': {
                              maxWidth: '150px'
                            }
                          }}
                        >
                          {dba}
                        </Typography>
                      </CustomTooltip>
                    </Box>
                  </Stack>
                  {(!isCollapsed || dbaHovered ) && (
                    <Box sx={{ flexShrink: 0 }}>
                      <CopyTextIconButton textToCopy={dba || ''} dispatchText="D.B.A" />
                    </Box>
                  )}
                </Stack>
              )}
            </Stack>
          </Stack>
        </ListItem>
        {(merchant?.email?.length || merchant?.phone?.length || web || application?.merchantBizEmails || application?.merchantBizPhones || taxId) && (
          isCollapsed
            ? <Collapse in={isHovered} timeout={600} easing="cubic-bezier(0.4, 0, 0.2, 1)" unmountOnExit>
              {merchantContactInfo}
            </Collapse>
            : merchantContactInfo
        )}
        {allOwners.length > 0 && (
          <Collapse
            in={!isCollapsed || Boolean(isHovered)}
            timeout={600}
            easing="cubic-bezier(0.4, 0, 0.2, 1)"
            unmountOnExit={isCollapsed}
          >
            <Stack spacing={0.5} textAlign="left" sx={{ mt: 0.5, '& .MuiListItem-root': { p: 0 } }}>
              <Stack spacing={0.5} alignItems="center">
                <Typography color="#706CE6">OWNER INFO</Typography>
              </Stack>
              <Stack
                spacing={0}
                sx={
                  disableContactInfoClamp
                    ? { height: 'auto', maxHeight: 'none', overflowY: 'visible' }
                    : { maxHeight: 120, overflowY: 'auto', overflowX: 'hidden' }
                }
              >
                {allOwners.map((userowner) => {
                  const bizPhone = userowner.phone;
                  const fullName = userowner.fullName?.trim() ?? '';
                  const emails: string[] = userowner.email?.trim() ? [userowner.email] : [];
                  const phones: string[] = userowner.phone?.trim() ? [userowner.phone] : [];

                  return (
                    <Stack key={userowner.id} spacing={0} sx={{ display: 'contents' }}>
                      {fullName && (
                        <ListItem disableGutters alignItems="center" sx={{ m: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32, mr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <UserOutlined style={{ fontSize: 12, color: 'slateBlue' }} />
                          </ListItemIcon>
                          <Stack justifyContent="center" flex={1} sx={{ minWidth: 0, height: 22 }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              width="100%"
                              gap={0.5}
                              onMouseEnter={() => setHoveredOwnerId(userowner.id)}
                              onMouseLeave={() => setHoveredOwnerId(null)}
                            >
                              <CustomTooltip title={fullName} placement="top">
                                <Typography noWrap sx={{ fontSize: 12, lineHeight: 1.2 }}>
                                  {fullName}
                                </Typography>
                              </CustomTooltip>
                              {isCollapsed ? (
                                <Box
                                  sx={{
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    minWidth: 40,
                                    visibility: hoveredOwnerId === userowner.id ? 'visible' : 'hidden'
                                  }}
                                >
                                  <CopyTextIconButton textToCopy={fullName} dispatchText="Owner Name" />
                                </Box>
                              ) : (
                                <CopyTextIconButton textToCopy={fullName} dispatchText="Owner Name" />
                              )}
                            </Stack>
                          </Stack>
                        </ListItem>
                      )}
                      {emails.length > 0 && (
                        <MerchantContactInfoList items={emails} label="Email" isCollapsed={isCollapsed} />
                      )}
                      {phones.length > 0 && (
                        <MerchantContactInfoList
                          items={phones}
                          label="Phone"
                          isCollapsed={isCollapsed}
                          renderer={(val) => <PhoneNoRenderer value={val} />}
                        />
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          </Collapse>
        )}
      </Stack>
    </List>
  );
}
